import { useState } from 'react';
import { modificationCategories, modificationFormSchema } from '@greasemap/validation';
import {
  useAddModification,
  useDeleteModification,
  useUpdateModification,
} from '../../garage/mutations';
import { formatMoney } from '../../garage/format';
import type { GarageSpend, Modification } from '../../garage/types';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Heading,
  Select,
  Stack,
  Text,
  TextArea,
  TextField,
} from '../../ui';

/** Props for {@link ModificationsSection}. */
export interface ModificationsSectionProps {
  readonly vehicleId: string;
  readonly modifications: readonly Modification[];
  readonly spend: GarageSpend;
}

const CATEGORY_OPTIONS = modificationCategories.map((value) => ({ value, label: value }));

const BLANK_FORM = {
  category: 'Engine',
  title: '',
  cost: '',
  installedDate: '',
  installer: '',
  notes: '',
};

/** Form values as text, before validation. */
type FormValues = typeof BLANK_FORM;

/** Fills the form from an existing modification so an edit starts from what is stored. */
function formFrom(modification: Modification): FormValues {
  return {
    category: modification.category,
    title: modification.title,
    cost: modification.cost == null ? '' : String(modification.cost),
    installedDate: modification.installedDate ?? '',
    installer: modification.installer ?? '',
    notes: modification.notes ?? '',
  };
}

/**
 * Everything installed on a vehicle, plus what it cost.
 *
 * The same panel adds a new modification or edits an existing one: opening it from a row's Edit
 * button fills it from that row and sends the row's `version` back on save, so a concurrent change
 * is caught rather than silently overwritten.
 *
 * @param props - Component props.
 * @returns The modifications section.
 */
export function ModificationsSection({
  vehicleId,
  modifications,
  spend,
}: ModificationsSectionProps): React.JSX.Element {
  const add = useAddModification(vehicleId);
  const update = useUpdateModification(vehicleId);
  const remove = useDeleteModification(vehicleId);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Modification | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormValues>(BLANK_FORM);

  const isOpen = open || editing !== null;
  const isSaving = add.isPending || update.isPending;

  function toggleAdd(): void {
    setError(null);

    if (isOpen) {
      setOpen(false);
      setEditing(null);
      return;
    }

    setEditing(null);
    setForm(BLANK_FORM);
    setOpen(true);
  }

  function startEdit(modification: Modification): void {
    setError(null);
    setOpen(false);
    setForm(formFrom(modification));
    setEditing(modification);
  }

  function closeForm(): void {
    setOpen(false);
    setEditing(null);
    setForm(BLANK_FORM);
    setError(null);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = modificationFormSchema.safeParse({
      category: form.category,
      title: form.title,
      cost: form.cost === '' ? undefined : Number(form.cost),
      installedDate: form.installedDate === '' ? undefined : form.installedDate,
      installer: form.installer,
      notes: form.notes,
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the details and try again.');
      return;
    }

    try {
      if (editing !== null) {
        await update.mutateAsync({
          modificationId: editing.id,
          body: {
            category: parsed.data.category,
            title: parsed.data.title,
            notes: parsed.data.notes ?? null,
            cost: parsed.data.cost ?? null,
            installedDate: parsed.data.installedDate ?? null,
            installer: parsed.data.installer ?? null,
            version: editing.version,
          },
        });
      } else {
        await add.mutateAsync({
          category: parsed.data.category,
          title: parsed.data.title,
          notes: parsed.data.notes ?? null,
          cost: parsed.data.cost ?? null,
          installedDate: parsed.data.installedDate ?? null,
          installer: parsed.data.installer ?? null,
          version: null,
        });
      }

      closeForm();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save that modification.');
    }
  }

  return (
    <Stack gap="lg">
      <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
        <Heading level={2} size="lg">
          Modifications
        </Heading>
        <Button variant="primary" onClick={toggleAdd}>
          {open ? 'Close' : 'Add a modification'}
        </Button>
      </Stack>

      <Stack direction="row" gap="sm" wrap align="center">
        <Badge tone="accent">{formatMoney(spend.total)} spent</Badge>
        {spend.uncostedCount > 0 && (
          <Badge tone="warning">plus {spend.uncostedCount} with no cost recorded</Badge>
        )}
      </Stack>

      {isOpen && (
        <Card ariaLabel={editing !== null ? 'Edit modification' : 'New modification'}>
          <form onSubmit={(event) => void submit(event)}>
            <Stack gap="lg">
              <Select
                label="Category"
                options={CATEGORY_OPTIONS}
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              />
              <TextField
                label="What did you do?"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
              <TextField
                label="Cost"
                inputMode="decimal"
                value={form.cost}
                onChange={(event) => setForm({ ...form, cost: event.target.value })}
                hint="Leave blank if you would rather not record it."
              />
              <TextField
                label="Installed on"
                type="date"
                value={form.installedDate}
                onChange={(event) => setForm({ ...form, installedDate: event.target.value })}
              />
              <TextField
                label="Installed by"
                value={form.installer}
                onChange={(event) => setForm({ ...form, installer: event.target.value })}
              />
              <TextArea
                label="Notes"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />

              {error !== null && (
                <p className="gm-field-error" role="alert">
                  {error}
                </p>
              )}

              <Stack direction="row" gap="sm" wrap>
                <Button type="submit" variant="primary" disabled={isSaving}>
                  {editing !== null ? 'Save changes' : 'Save modification'}
                </Button>
                {editing !== null && (
                  <Button type="button" onClick={closeForm}>
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </form>
        </Card>
      )}

      {modifications.length === 0 ? (
        <EmptyState
          title="Nothing recorded yet"
          description="Log what you have changed so the history travels with the car."
        />
      ) : (
        <Stack gap="none">
          {modifications.map((modification) => (
            <div key={modification.id} className="gm-row">
              <Stack gap="xs">
                <Stack direction="row" gap="sm" align="center" wrap>
                  <Text weight="semibold">{modification.title}</Text>
                  <Badge tone="neutral">{modification.category}</Badge>
                </Stack>
                <Text size="sm" muted>
                  {formatMoney(modification.cost)}
                  {modification.installedDate == null ? '' : ` · ${modification.installedDate}`}
                  {modification.installer == null ? '' : ` · ${modification.installer}`}
                </Text>
                {modification.notes != null && (
                  <Text size="sm" muted>
                    {modification.notes}
                  </Text>
                )}
              </Stack>
              <div style={{ marginLeft: 'auto' }}>
                <Stack direction="row" gap="xs" wrap>
                  <Button
                    onClick={() => startEdit(modification)}
                    aria-label={`Edit ${modification.title}`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setPendingDelete(modification.id)}
                    aria-label={`Remove ${modification.title}`}
                  >
                    Remove
                  </Button>
                </Stack>
              </div>
            </div>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove this modification?"
        message="It disappears from the vehicle history and from your spend totals."
        confirmLabel="Remove"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) {
            remove.mutate(pendingDelete);
          }

          setPendingDelete(null);
        }}
      />
    </Stack>
  );
}
